import React, { useContext, useEffect, useReducer, useState, useMemo } from "react";
import {
  Table, Label, Message, Icon, Loader, Segment, Dimmer,
  Button, Pagination, Dropdown, Input,
} from "semantic-ui-react";
import { Link } from "react-router-dom";
import { authContext } from "../../containers/AuthProvider";
import { searchOrders } from "../../libs/requests/orderSearch";
import { fetchVodafoneOrders } from "../../libs/requests/vodafoneOrderSearch";
import { getPartners } from "../../libs/requests/getPartnerDetails";
import updatePartnerAsset from "../../libs/requests/updatePartnerAsset";
import { getSupplier } from "../../libs/sessionVariable";
import { isOnboarded } from "../../libs/utils";
import moment from "moment";

const PAGE_SIZE = 10;

const SUPPLIER_COLOUR = { ZEN: "teal", Giganet: "green", Vodafone: "red" };

const STATUS_COLOUR = {
  Pending: "yellow", Acknowledged: "olive", Committed: "teal",
  Completed: "green", Rejected: "red", Cancelled: "grey",
  Delayed: "orange", Cancelling: "pink", "In Progress": "blue",
  Accepted: "green", Failed: "red",
};

const FULFILMENT_STATE_OPTIONS = [
  { key: "all",        value: 0,   text: "All States" },
  { key: "pending",    value: 1,   text: "Pending" },
  { key: "acknowledged", value: 2, text: "Acknowledged" },
  { key: "committed",  value: 4,   text: "Committed" },
  { key: "completed",  value: 8,   text: "Completed" },
  { key: "rejected",   value: 16,  text: "Rejected" },
  { key: "cancelled",  value: 32,  text: "Cancelled" },
  { key: "delayed",    value: 64,  text: "Delayed" },
  { key: "inFlight",   value: 71,  text: "In Flight" },
  { key: "cancelling", value: 128, text: "Cancelling" },
];

// Maps ZEN's numeric fulfilmentState filter values to Vodafone string status equivalents.
// Vodafone lifecycle: Pending → Accepted (≈Acknowledged) → Committed → Completed.
// 0 = All (null means no filter), unknown values return [] (show nothing for that Vodafone state).
const VODAFONE_STATUS_FOR_STATE = {
  0:   null,                                 // All — no filter
  1:   ["Pending"],
  2:   ["Accepted"],                         // Vodafone "Accepted" ≈ ZEN "Acknowledged"
  4:   ["Committed"],
  8:   ["Completed"],
  16:  ["Rejected"],
  32:  ["Cancelled", "Cancelling"],
  64:  [],                                   // Delayed — no Vodafone equivalent
  71:  ["Pending", "Accepted", "Committed"], // In Flight
  128: ["Cancelling"],
};

const HIGH_TIDE_PARTNERS = [
  "OneHelix", "E-volve Solutions", "IT4 Automation",
  "Community Waves", "Hightide", "Netix", "Countryside Connections",
];

function mapZen(order, fulfilmentState) {
  return {
    reference: order.reference ?? order.zenReference,
    supplier: "ZEN",
    status: order.fulfilmentStatus ?? order.status ?? "Unknown",
    productDescription: order.productDescription ?? "—",
    date: order.orderDate,
    fulfilmentState,
    assignedTo: order.assignedTo ?? "",
    isZen: true,
  };
}

function mapVodafone(order) {
  return {
    reference: order.reference,
    supplier: "Vodafone",
    status: order.status ?? "Unknown",
    productDescription: order.productDescription ?? "—",
    address: order.address ?? "—",
    date: order.activationDate,
    assignedTo: order.orderedBy ?? "",
    isZen: false,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "LOADING": return { ...state, isLoading: true, error: null };
    case "LOADED":  return { ...state, isLoading: false, orders: action.orders, error: null };
    case "ERROR":   return { ...state, isLoading: false, error: action.error };
    case "PAGE":    return { ...state, page: action.page };
    case "ASSIGN":  return {
      ...state,
      orders: state.orders.map(o =>
        o.reference === action.ref ? { ...o, assignedTo: action.partner } : o
      ),
    };
    default: return state;
  }
}

export default function UnifiedOrdersTable({ pendingOnly = false }) {
  const { permissions } = useContext(authContext);
  const [isVodafoneOnboarded, setIsVodafoneOnboarded] = useState(false);

  useEffect(() => {
    isOnboarded('Vodafone')
      .then(setIsVodafoneOnboarded)
      .catch(() => setIsVodafoneOnboarded(false));
  }, [permissions]);

  const [state, dispatch] = useReducer(reducer, { orders: [], isLoading: false, error: null, page: 1 });
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [fulfilmentState, setFulfilmentState] = useState(pendingOnly ? 1 : 71);

  // Admin partner assignment (mirrors SearchServices / SearchOrders)
  const [partners, setPartners] = useState([]);
  const [isHightide, setIsHightide] = useState(false);
  const [filterByPartner, setFilterByPartner] = useState("");
  const [assignMsg, setAssignMsg] = useState(null);

  useEffect(() => { load(); }, [pendingOnly, fulfilmentState, isVodafoneOnboarded]);

  async function load() {
    dispatch({ type: "LOADING" });
    try {
      const [zenResult, vodafoneRaw] = await Promise.all([
        searchOrders("", fulfilmentState),
        isVodafoneOnboarded
          ? fetchVodafoneOrders().catch(() => [])
          : Promise.resolve([]),
      ]);

      const zenOrders = (zenResult?.orders ?? []).map(o => mapZen(o, fulfilmentState));
      const vodafoneOrders = Array.isArray(vodafoneRaw)
        ? vodafoneRaw.filter(o => !pendingOnly || o.status === "Pending").map(mapVodafone)
        : [];

      const merged = [...zenOrders, ...vodafoneOrders].sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date) - new Date(a.date);
      });

      dispatch({ type: "LOADED", orders: merged });

      if (permissions?.admin) {
        const supplier = await getSupplier("", true);
        const isHt = supplier === "Faelix";
        setIsHightide(isHt);
        setPartners(isHt ? HIGH_TIDE_PARTNERS : await getPartners());
      }
    } catch {
      dispatch({ type: "ERROR", error: "Failed to load orders." });
    }
  }

  const partnerDropdownOptions = useMemo(() => {
    const list = Array.isArray(partners) && typeof partners[0] === "string"
      ? partners
      : partners.map(p => p.Partner ?? p).filter(Boolean);

    const opts = [...list]
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ key: v, text: v, value: v }));
    opts.unshift({ key: "all", text: "All", value: "" });
    return opts;
  }, [partners]);

  async function handleAssign(reference, partner, orderSupplier) {
    setAssignMsg({ type: "info", text: `Assigning ${reference} to ${partner}…` });
    const formattedPartner = isHightide ? `Hightide|${partner}` : partner;
    const supplier = (orderSupplier ?? "ZEN").toUpperCase();
    const result = await updatePartnerAsset({ reference, partner: formattedPartner, supplier });
    if (result) {
      dispatch({ type: "ASSIGN", ref: reference, partner: formattedPartner });
      setAssignMsg({ type: "success", text: `Assigned ${reference} to ${partner}.` });
    } else {
      setAssignMsg({ type: "warning", text: `Failed to assign ${reference} to ${partner}.` });
    }
  }

  const hasVodafone = state.orders.some(o => o.supplier === "Vodafone");
  const supplierOptions = [
    { key: "all",      text: "All suppliers", value: "all" },
    { key: "zen",      text: "ZEN",           value: "zen" },
    ...(isVodafoneOnboarded ? [{ key: "vodafone", text: "Vodafone", value: "vodafone" }] : []),
  ];

  const filtered = state.orders.filter(o => {
    const matchesSupplier = supplierFilter === "all" || o.supplier.toLowerCase() === supplierFilter;
    const matchesSearch   = !searchTerm      || o.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPartner  = !filterByPartner || o.assignedTo?.toLowerCase().includes(filterByPartner.toLowerCase());

    let matchesState = true;
    if (!o.isZen && fulfilmentState !== 0) {
      const allowed = VODAFONE_STATUS_FOR_STATE[fulfilmentState];
      // null means "All" (0 already caught above); empty array means no Vodafone equivalent
      matchesState = allowed === null || (Array.isArray(allowed) && allowed.includes(o.status));
    }

    return matchesSupplier && matchesSearch && matchesPartner && matchesState;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible    = filtered.slice((state.page - 1) * PAGE_SIZE, state.page * PAGE_SIZE);

  if (state.isLoading) {
    return (
      <Segment>
        <Dimmer active inverted><Loader>Loading orders…</Loader></Dimmer>
      </Segment>
    );
  }

  if (state.error) {
    return <Message negative><Message.Header>{state.error}</Message.Header></Message>;
  }

  return (
    <>
      {assignMsg && (
        <Message
          positive={assignMsg.type === "success"}
          warning={assignMsg.type === "warning"}
          info={assignMsg.type === "info"}
          onDismiss={() => setAssignMsg(null)}
        >
          {assignMsg.text}
        </Message>
      )}

      <div style={{ display: "flex", gap: "16px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" }}>
        <Input
          icon="search"
          placeholder="Search by reference…"
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); dispatch({ type: "PAGE", page: 1 }); }}
        />

        {!pendingOnly && (
          <Dropdown
            selection
            options={FULFILMENT_STATE_OPTIONS}
            value={fulfilmentState}
            onChange={(_, { value }) => { setFulfilmentState(value); dispatch({ type: "PAGE", page: 1 }); }}
            style={{ minWidth: "150px" }}
          />
        )}

        {permissions?.admin && partners.length > 0 && (
          <Dropdown
            placeholder="All (Show everything)"
            search
            selection
            clearable
            options={partnerDropdownOptions}
            value={filterByPartner}
            onChange={(_, { value }) => { setFilterByPartner(value ?? ""); dispatch({ type: "PAGE", page: 1 }); }}
            style={{ minWidth: "180px" }}
          />
        )}

        {isVodafoneOnboarded && (
          <>
            <span style={{ fontWeight: 600 }}>Supplier:</span>
            <Dropdown
              inline
              options={supplierOptions}
              value={supplierFilter}
              onChange={(_, { value }) => { setSupplierFilter(value); dispatch({ type: "PAGE", page: 1 }); }}
            />
          </>
        )}

        <Button icon labelPosition="left" size="small" onClick={load}>
          <Icon name="refresh" /> Refresh
        </Button>
      </div>

      {filtered.length === 0 ? (
        <Message info><Icon name="info circle" /> No orders found.</Message>
      ) : (
        <>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Reference</Table.HeaderCell>
                <Table.HeaderCell>Supplier</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Product</Table.HeaderCell>
                {hasVodafone && <Table.HeaderCell>Address</Table.HeaderCell>}
                <Table.HeaderCell>Date</Table.HeaderCell>
                {permissions?.admin && <Table.HeaderCell>Assigned To</Table.HeaderCell>}
                {permissions?.admin && <Table.HeaderCell>Actions</Table.HeaderCell>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {visible.map((order, i) => (
                <Table.Row key={`${order.reference}-${i}`}>
                  <Table.Cell>
                    {order.isZen
                      ? (
                        <Link to={{
                          pathname: `/Order/${order.reference}`,
                          search: `?fulfilmentState=${order.fulfilmentState}${order.date ? `&orderDate=${encodeURIComponent(order.date)}` : ""}`,
                        }}>
                          {order.reference}
                        </Link>
                      )
                      : order.supplier === "Vodafone"
                        ? <Link to={`/VodafoneOrder/${order.reference}`}>{order.reference}</Link>
                        : order.reference}
                  </Table.Cell>
                  <Table.Cell>
                    <Label size="small" color={SUPPLIER_COLOUR[order.supplier] ?? "grey"}>
                      {order.supplier}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Label size="small" color={STATUS_COLOUR[order.status] ?? "grey"}>
                      {order.status}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>{order.productDescription}</Table.Cell>
                  {hasVodafone && <Table.Cell>{order.address}</Table.Cell>}
                  <Table.Cell>
                    {order.date ? moment(order.date).format("DD/MM/YYYY") : "—"}
                  </Table.Cell>
                  {permissions?.admin && (
                    <Table.Cell data-testid="assigned-to-cell">
                      {order.assignedTo || "—"}
                    </Table.Cell>
                  )}
                  {permissions?.admin && (
                    <Table.Cell>
                      {(order.isZen || order.supplier === "Vodafone") && partners.length > 0 && (
                        <Dropdown
                          text="Assign"
                          fluid
                          button
                          className="icon"
                          scrolling
                          style={{ minWidth: "130px", position: "relative" }}
                        >
                          <Dropdown.Menu style={{
                            maxHeight: "300px", overflowY: "auto",
                            minWidth: "200px", position: "absolute",
                            left: 0, zIndex: 1001, marginTop: "5px",
                          }}>
                            {(Array.isArray(partners) && typeof partners[0] === "string"
                              ? partners
                              : partners.map(p => p.Partner ?? p).filter(Boolean)
                            ).map(name => (
                              <Dropdown.Item
                                key={name}
                                onClick={() => handleAssign(order.reference, name, order.supplier)}
                                style={{ padding: "10px 15px", whiteSpace: "normal", wordBreak: "break-word" }}
                              >
                                {name}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          {totalPages > 1 && (
            <Pagination
              activePage={state.page}
              totalPages={totalPages}
              onPageChange={(_, { activePage }) => dispatch({ type: "PAGE", page: activePage })}
            />
          )}
        </>
      )}
    </>
  );
}
